import { getToken, clearToken } from '../utils/authToken';
/**
 * PublicGallery - Galería Pública de Apps
 * Donde los usuarios pueden ver, clonar y compartir apps de otros
 */
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  X, Search, Filter, Globe, Eye, Heart, Copy, ExternalLink,
  Loader2, Rocket, Tag, TrendingUp, Clock, Sparkles, ChevronDown,
  LayoutDashboard, Gamepad2, Wrench, Globe2, ChevronRight, Star, Trophy
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';
import { AppCard, AppViewerModal } from './MyApps';
import StarRating from './StarRating';

import { API_URL } from '../config';

// Configuración de categorías con iconos y colores
const CATEGORIES_CONFIG = {
  dashboard: {
    icon: LayoutDashboard,
    color: 'from-blue-500 to-cyan-500',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    labelEs: 'Dashboards',
    labelEn: 'Dashboards'
  },
  games: {
    icon: Gamepad2,
    color: 'from-pink-500 to-rose-500',
    bgColor: 'bg-pink-500/10',
    borderColor: 'border-pink-500/30',
    labelEs: 'Juegos',
    labelEn: 'Games'
  },
  utilities: {
    icon: Wrench,
    color: 'from-amber-500 to-orange-500',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30',
    labelEs: 'Utilidades',
    labelEn: 'Utilities'
  },
  landing: {
    icon: Globe2,
    color: 'from-green-500 to-emerald-500',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/30',
    labelEs: 'Landing Pages',
    labelEn: 'Landing Pages'
  }
};

// Componente para mostrar una categoría destacada
const FeaturedCategory = ({ category, apps, onView, onClone, isSpanish }) => {
  const config = CATEGORIES_CONFIG[category];
  if (!config || apps.length === 0) return null;
  
  const Icon = config.icon;
  const label = isSpanish ? config.labelEs : config.labelEn;
  
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-8 h-8 rounded-lg bg-gradient-to-r ${config.color} flex items-center justify-center`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        <h3 className="text-sm font-semibold text-white">{label}</h3>
        <span className="text-xs text-zinc-500">({apps.length})</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {apps.slice(0, 3).map(app => (
          <MiniAppCard key={app.id} app={app} onView={onView} onClone={onClone} config={config} />
        ))}
      </div>
    </div>
  );
};

// Mini tarjeta de app para la sección destacada
const MiniAppCard = ({ app, onView, onClone, config, cloneText }) => {
  return (
    <div 
      className={`group relative rounded-xl overflow-hidden border ${config.borderColor} ${config.bgColor} hover:border-white/30 transition-all cursor-pointer`}
      onClick={() => onView(app)}
    >
      {/* Thumbnail */}
      <div className="h-24 bg-zinc-800 overflow-hidden">
        {app.thumbnail ? (
          <img src={app.thumbnail} alt={app.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
        ) : (
          <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${config.color} opacity-30`}>
            <config.icon className="w-8 h-8 text-white/50" />
          </div>
        )}
      </div>
      
      {/* Info */}
      <div className="p-2">
        <h4 className="text-xs font-medium text-white truncate">{app.name}</h4>
        <div className="flex items-center gap-2 mt-1">
          {app.average_rating > 0 && (
            <span className="text-[10px] text-amber-400 flex items-center gap-0.5">
              <Star className="w-3 h-3 fill-amber-400" /> {app.average_rating?.toFixed(1)}
            </span>
          )}
          <span className="text-[10px] text-zinc-500 flex items-center gap-1">
            <Eye className="w-3 h-3" /> {app.views_count || 0}
          </span>
          <span className="text-[10px] text-zinc-500 flex items-center gap-1">
            <Heart className="w-3 h-3" /> {app.likes_count || 0}
          </span>
        </div>
      </div>
      
      {/* Quick Clone Button */}
      <button
        onClick={(e) => { e.stopPropagation(); onClone(app); }}
        className="absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-black/80 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
        title={cloneText}
      >
        <Copy className="w-3 h-3 text-white" />
      </button>
    </div>
  );
};

// Componente para mostrar Top Apps
const TopAppsSection = ({ apps, onView, onClone, isSpanish, loading }) => {
  const cloneText = isSpanish ? 'Clonar' : 'Clone';
  
  if (loading) {
    return (
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="w-5 h-5 text-amber-400" />
          <h2 className="text-base font-semibold text-white">
            {isSpanish ? 'Top Apps' : 'Top Apps'}
          </h2>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
        </div>
      </div>
    );
  }
  
  if (!apps || apps.length === 0) return null;
  
  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
          <Trophy className="w-4 h-4 text-white" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-white">
            {isSpanish ? 'Top Apps' : 'Top Apps'}
          </h2>
          <p className="text-xs text-zinc-500">
            {isSpanish ? 'Las mejor calificadas por la comunidad' : 'Highest rated by the community'}
          </p>
        </div>
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
        {apps.slice(0, 5).map((app, index) => (
          <div 
            key={app.id}
            className="group relative rounded-xl overflow-hidden border border-amber-500/30 bg-amber-500/5 hover:border-amber-400/50 transition-all cursor-pointer"
            onClick={() => onView(app)}
          >
            {/* Rank Badge */}
            <div className="absolute top-2 left-2 z-10 w-6 h-6 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center text-white text-xs font-bold shadow-lg">
              {index + 1}
            </div>
            
            {/* Thumbnail */}
            <div className="h-24 bg-zinc-800 overflow-hidden">
              {app.thumbnail ? (
                <img src={app.thumbnail} alt={app.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-900/50 to-orange-900/50">
                  <Trophy className="w-8 h-8 text-amber-400/50" />
                </div>
              )}
            </div>
            
            {/* Info */}
            <div className="p-2">
              <h4 className="text-xs font-medium text-white truncate">{app.name}</h4>
              <div className="flex items-center gap-1 mt-1">
                <StarRating rating={app.average_rating || 0} size="xs" />
                <span className="text-[10px] text-zinc-500">({app.ratings_count || 0})</span>
              </div>
            </div>
            
            {/* Clone Button */}
            <button
              onClick={(e) => { e.stopPropagation(); onClone(app); }}
              className="absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-black/80 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
              title={cloneText}
            >
              <Copy className="w-3 h-3 text-white" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const PublicGallery = ({ isOpen, onClose }) => {
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  const [apps, setApps] = useState([]);
  const [featuredApps, setFeaturedApps] = useState({});
  const [topApps, setTopApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingFeatured, setLoadingFeatured] = useState(true);
  const [loadingTopApps, setLoadingTopApps] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [popularTags, setPopularTags] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [showViewer, setShowViewer] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);
  const [showFeatured, setShowFeatured] = useState(true);

  useEffect(() => {
    if (isOpen) {
      loadApps(true);
      loadTags();
      loadFeatured();
      loadTopApps();
    }
  }, [isOpen, sortBy, selectedTag]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTopApps = async () => {
    setLoadingTopApps(true);
    try {
      const response = await axios.get(`${API_URL}/api/published-apps/top?limit=10`);
      setTopApps(response.data.apps || []);
    } catch (error) {
      console.warn('Could not load top apps');
    } finally {
      setLoadingTopApps(false);
    }
  };

  const loadFeatured = async () => {
    setLoadingFeatured(true);
    try {
      const response = await axios.get(`${API_URL}/api/published-apps/featured/categories`);
      setFeaturedApps(response.data.featured || {});
    } catch (error) {
      console.warn('Could not load featured apps');
    } finally {
      setLoadingFeatured(false);
    }
  };

  const loadApps = async (reset = false) => {
    if (reset) {
      setPage(0);
      setLoading(true);
    }
    
    try {
      const skip = reset ? 0 : page * 20;
      const params = new URLSearchParams({
        skip: skip.toString(),
        limit: '20',
        sort_by: sortBy
      });
      
      if (selectedTag) params.append('tag', selectedTag);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axios.get(`${API_URL}/api/published-apps/gallery?${params}`);
      
      if (reset) {
        setApps(response.data.apps || []);
      } else {
        setApps(prev => [...prev, ...(response.data.apps || [])]);
      }
      setHasMore(response.data.has_more);
      if (!reset) setPage(prev => prev + 1);
    } catch (error) {
      toast.error(isSpanish ? 'Error al cargar galería' : 'Error loading gallery');
    } finally {
      setLoading(false);
    }
  };

  const loadTags = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/published-apps/tags`);
      setPopularTags(response.data.tags || []);
    } catch (error) {
      // Silent fail for tags
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadApps(true);
  };

  const handleView = async (app) => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/published-apps/view/${app.slug}`, {});
      setSelectedApp(response.data.app);
      setShowViewer(true);
    } catch (error) {
      toast.error(isSpanish ? 'Error al cargar app' : 'Error loading app');
    }
  };

  const handleClone = async (app) => {
    const token = getToken();
    if (!token) {
      toast.error(isSpanish ? 'Inicia sesión para clonar' : 'Login to clone');
      return;
    }
    
    try {
      await axios.post(
        `${API_URL}/api/published-apps/${app.id}/clone`,
        {},
        {}
      );
      toast.success(isSpanish ? '¡App clonada a tus proyectos!' : 'App cloned to your projects!');
      setShowViewer(false);
    } catch (error) {
      toast.error(isSpanish ? 'Error al clonar' : 'Error cloning app');
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      
      <div 
        className="relative w-full max-w-5xl h-[85vh] bg-zinc-900 border border-zinc-700 rounded-2xl overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-zinc-700 bg-gradient-to-r from-purple-900/30 to-pink-900/30">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-purple-500/30 to-pink-500/30 flex items-center justify-center">
                <Globe className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">
                  {isSpanish ? 'Apps Públicas' : 'Public Apps'}
                </h2>
                <p className="text-xs text-zinc-400">
                  {isSpanish ? 'Descubre apps creadas por la comunidad' : 'Discover apps created by the community'}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-zinc-700 rounded-lg transition-colors">
              <X className="w-5 h-5 text-zinc-400" />
            </button>
          </div>
          
          {/* Search and Filters */}
          <div className="flex items-center gap-3">
            <form onSubmit={handleSearch} className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder={isSpanish ? 'Buscar apps...' : 'Search apps...'}
                className="w-full pl-9 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-purple-500"
              />
            </form>
            
            {/* Sort */}
            <div className="relative">
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                className="appearance-none px-4 py-2 pr-8 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white focus:outline-none focus:border-purple-500 cursor-pointer"
              >
                <option value="recent">{isSpanish ? 'Recientes' : 'Recent'}</option>
                <option value="popular">{isSpanish ? 'Populares' : 'Popular'}</option>
                <option value="views">{isSpanish ? 'Más vistas' : 'Most viewed'}</option>
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
            </div>
          </div>
          
          {/* Tags */}
          {popularTags.length > 0 && (
            <div className="flex items-center gap-2 mt-3 overflow-x-auto pb-1">
              <button
                onClick={() => { setSelectedTag(''); loadApps(true); }}
                className={`px-3 py-1 text-xs rounded-full whitespace-nowrap transition-colors ${
                  !selectedTag ? 'bg-purple-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'
                }`}
              >
                {isSpanish ? 'Todas' : 'All'}
              </button>
              {popularTags.slice(0, 10).map(tag => (
                <button
                  key={tag.name}
                  onClick={() => { setSelectedTag(tag.name); }}
                  className={`px-3 py-1 text-xs rounded-full whitespace-nowrap transition-colors flex items-center gap-1 ${
                    selectedTag === tag.name ? 'bg-purple-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'
                  }`}
                >
                  <Tag className="w-3 h-3" />
                  {tag.name}
                  <span className="text-[10px] opacity-60">({tag.count})</span>
                </button>
              ))}
            </div>
          )}
        </div>
        
        {/* Apps Grid */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* Top Apps Section - Show first when no search/filter */}
          {!searchQuery && !selectedTag && topApps.length > 0 && (
            <TopAppsSection
              apps={topApps}
              onView={handleView}
              onClone={handleClone}
              isSpanish={isSpanish}
              loading={loadingTopApps}
            />
          )}
          
          {/* Featured Categories Section */}
          {showFeatured && !searchQuery && !selectedTag && Object.keys(featuredApps).some(k => featuredApps[k]?.length > 0) && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-400" />
                  <h2 className="text-base font-semibold text-white">
                    {isSpanish ? 'Apps Destacadas' : 'Featured Apps'}
                  </h2>
                </div>
                <button
                  onClick={() => setShowFeatured(false)}
                  className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
                >
                  {isSpanish ? 'Ocultar' : 'Hide'}
                </button>
              </div>
              
              {loadingFeatured ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries(CATEGORIES_CONFIG).map(([category]) => (
                    <FeaturedCategory
                      key={category}
                      category={category}
                      apps={featuredApps[category] || []}
                      onView={handleView}
                      onClone={handleClone}
                      isSpanish={isSpanish}
                    />
                  ))}
                </div>
              )}
              
              {/* Divider */}
              <div className="border-t border-zinc-700 mt-6 pt-4">
                <h3 className="text-sm font-medium text-zinc-400 mb-4">
                  {isSpanish ? 'Todas las Apps' : 'All Apps'}
                </h3>
              </div>
            </div>
          )}
          
          {/* Show featured toggle when hidden */}
          {!showFeatured && !searchQuery && !selectedTag && (
            <button
              onClick={() => setShowFeatured(true)}
              className="mb-4 text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1"
            >
              <Star className="w-3 h-3" />
              {isSpanish ? 'Mostrar Apps Destacadas' : 'Show Featured Apps'}
            </button>
          )}
          
          {loading && apps.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
            </div>
          ) : apps.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Sparkles className="w-16 h-16 text-zinc-700 mb-4" />
              <h3 className="text-lg font-medium text-zinc-400 mb-2">
                {isSpanish ? 'No hay apps públicas' : 'No public apps yet'}
              </h3>
              <p className="text-sm text-zinc-500 max-w-sm">
                {isSpanish 
                  ? '¡Sé el primero en publicar una app!'
                  : 'Be the first to publish an app!'}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {apps.map(app => (
                  <AppCard
                    key={app.id}
                    app={app}
                    onView={handleView}
                    onEdit={() => {}}
                    onClone={handleClone}
                    onDelete={() => {}}
                    onTogglePublic={() => {}}
                    isOwner={false}
                  />
                ))}
              </div>
              
              {/* Load More */}
              {hasMore && (
                <div className="flex justify-center mt-6">
                  <button
                    onClick={() => loadApps(false)}
                    disabled={loading}
                    className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      isSpanish ? 'Cargar más' : 'Load more'
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* App Viewer Modal */}
      <AppViewerModal
        app={selectedApp}
        isOpen={showViewer}
        onClose={() => setShowViewer(false)}
        onClone={handleClone}
      />
    </div>,
    document.body
  );
};

export default PublicGallery;
